import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, MessageCircleQuestion } from 'lucide-react';

const faqs = [
  {
    question: "What is your typical project timeline?",
    answer: "Our project timelines vary depending on complexity. A standard web application MVP takes 8-12 weeks, while larger enterprise software projects can take 4-6 months. We always provide a detailed timeline during the discovery phase."
  },
  {
    question: "How do you ensure the security of our data?",
    answer: "Security is built into our core architecture. We follow strict ISO 27001 guidelines, use end-to-end encryption, implement role-based access control, and conduct regular penetration testing on all delivered applications."
  },
  {
    question: "Do you provide ongoing support and maintenance?",
    answer: "Yes, absolutely! We offer various Service Level Agreements (SLAs) tailored to your needs, ensuring 99.99% uptime, regular security patches, and feature updates long after the initial launch."
  },
  {
    question: "What is your pricing model?",
    answer: "We offer both fixed-price contracts for well-defined scopes and Time & Materials (T&M) for dynamic projects that require ongoing agility. We are transparent with our pricing and work to maximize your ROI."
  },
  {
    question: "Can you integrate with our existing legacy systems?",
    answer: "Certainly. Our engineers have extensive experience building custom APIs and middleware to seamlessly connect modern applications with legacy ERP, CRM, and bespoke enterprise systems."
  }
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 bg-white relative">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-700 font-semibold text-sm mb-4">
            <MessageCircleQuestion size={18} /> Got Questions?
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
            Frequently Asked <span className="text-blue-600">Questions</span>
          </h2>
          <p className="text-lg text-slate-600">
            Find quick answers to common questions about our processes, pricing, and services.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={`border rounded-2xl transition-all duration-300 ${
                openIndex === index ? 'border-blue-200 bg-blue-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-blue-100 hover:bg-slate-50'
              }`}
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full flex items-center justify-between p-6 text-left focus:outline-none"
              >
                <span className="text-lg font-semibold text-slate-900 pr-8">{faq.question}</span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                  openIndex === index ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  {openIndex === index ? <Minus size={18} /> : <Plus size={18} />}
                </div>
              </button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 pt-0 text-slate-600 leading-relaxed border-t border-blue-100/50 mt-2">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
